import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Pencil, Trash2, UsersRound, X } from 'lucide-react';
import { get, post, patch, del } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Modal from './Modal';

const GROUP_TYPES = ['small_group', 'ministry', 'volunteer'];

const TYPE_LABELS = {
  small_group: 'Small Group',
  ministry: 'Ministry',
  volunteer: 'Volunteer',
};

const TYPE_STYLES = {
  small_group: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  ministry: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  volunteer: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

const EMPTY = {
  name: '',
  group_type: 'small_group',
  description: '',
  leader_id: '',
};

const inputCls =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</span>
      {children}
    </label>
  );
}

export default function Groups() {
  const { isPastor, isViewer } = useAuth();
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(null); // group object or {} for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Manage-members modal state
  const [managing, setManaging] = useState(null); // group object
  const [groupMembers, setGroupMembers] = useState([]);
  const [gmLoading, setGmLoading] = useState(false);
  const [gmError, setGmError] = useState('');
  const [addMemberId, setAddMemberId] = useState('');
  const [addRole, setAddRole] = useState('member');
  const [gmBusy, setGmBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search) params.search = search;
      if (typeFilter) params.group_type = typeFilter;
      setGroups(await get('/api/v1/groups', { params }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    get('/api/v1/members', { params: { limit: 500 } })
      .then(setMembers)
      .catch(() => {});
  }, []);

  const memberName = (id) => {
    const m = members.find((x) => x.id === id);
    return m ? `${m.first_name} ${m.last_name}` : '—';
  };

  const openNew = () => {
    setForm(EMPTY);
    setFormError('');
    setEditing({});
  };

  const openEdit = (g) => {
    setForm({
      name: g.name || '',
      group_type: g.group_type || 'small_group',
      description: g.description || '',
      leader_id: g.leader_id || '',
    });
    setFormError('');
    setEditing(g);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null) payload[k] = v;
    });
    try {
      if (editing.id) {
        await patch(`/api/v1/groups/${editing.id}`, payload);
      } else {
        await post('/api/v1/groups', payload);
      }
      setEditing(null);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? This also removes its memberships.`)) return;
    try {
      await del(`/api/v1/groups/${g.id}`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  // --- Manage members ---
  const loadGroupMembers = useCallback(async (groupId) => {
    setGmLoading(true);
    setGmError('');
    try {
      setGroupMembers(await get(`/api/v1/groups/${groupId}/members`));
    } catch (err) {
      setGmError(err.message);
    } finally {
      setGmLoading(false);
    }
  }, []);

  const openManage = (g) => {
    setManaging(g);
    setAddMemberId('');
    setAddRole('member');
    setGmError('');
    setGroupMembers([]);
    loadGroupMembers(g.id);
  };

  const addMember = async () => {
    if (!addMemberId) return;
    setGmBusy(true);
    setGmError('');
    try {
      await post(`/api/v1/groups/${managing.id}/members`, {
        member_id: addMemberId,
        role: addRole || 'member',
      });
      setAddMemberId('');
      setAddRole('member');
      await loadGroupMembers(managing.id);
      load();
    } catch (err) {
      setGmError(err.message);
    } finally {
      setGmBusy(false);
    }
  };

  const removeMember = async (memberId) => {
    setGmBusy(true);
    setGmError('');
    try {
      await del(`/api/v1/groups/${managing.id}/members/${memberId}`);
      await loadGroupMembers(managing.id);
      load();
    } catch (err) {
      setGmError(err.message);
    } finally {
      setGmBusy(false);
    }
  };

  const availableMembers = members.filter(
    (m) => !groupMembers.some((gm) => gm.member_id === m.id)
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
            Groups
          </h2>
          {isViewer && (
            <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">
              Read-only demo
            </span>
          )}
        </div>
        {!isViewer && (
          <button
            onClick={openNew}
            className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg px-3 py-2 flex items-center gap-1"
          >
            <Plus size={16} /> Add group
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search groups…"
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2"
        >
          <option value="">All types</option>
          {GROUP_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-slate-400">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-400 flex flex-col items-center gap-2">
          <UsersRound size={28} />
          No groups yet.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <div
              key={g.id}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">{g.name}</h3>
                  <span
                    className={`inline-block mt-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                      TYPE_STYLES[g.group_type] || 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {TYPE_LABELS[g.group_type] || g.group_type}
                  </span>
                </div>
                {!isViewer && (
                  <div className="flex gap-2 text-slate-400">
                    <button
                      onClick={() => openEdit(g)}
                      className="hover:text-brand-600"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    {isPastor && (
                      <button
                        onClick={() => remove(g)}
                        className="hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {g.description && (
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  {g.description}
                </p>
              )}

              <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                <span className="text-slate-400 dark:text-slate-500">Leader: </span>
                {g.leader_name || '—'}
              </div>

              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full px-2 py-0.5">
                  {g.member_count} member{g.member_count === 1 ? '' : 's'}
                </span>
              </div>

              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                <button
                  onClick={() => openManage(g)}
                  className="text-sm font-medium text-brand-600 hover:text-brand-700"
                >
                  Manage members
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal
          title={editing.id ? 'Edit group' : 'Add group'}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form onSubmit={save} className="space-y-3">
            {formError && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">
                {formError}
              </div>
            )}
            <Field label="Group name">
              <input
                required
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Type">
              <select
                className={inputCls}
                value={form.group_type}
                onChange={(e) => setForm({ ...form, group_type: e.target.value })}
              >
                {GROUP_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Leader">
              <select
                className={inputCls}
                value={form.leader_id}
                onChange={(e) => setForm({ ...form, leader_id: e.target.value })}
              >
                <option value="">— None —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.first_name} {m.last_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Description">
              <textarea
                rows={3}
                className={inputCls}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>
          </form>
        </Modal>
      )}

      {managing && (
        <Modal
          title={`Manage members — ${managing.name}`}
          onClose={() => setManaging(null)}
          footer={
            <button
              onClick={() => setManaging(null)}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            >
              Done
            </button>
          }
        >
          <div className="space-y-4">
            {gmError && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">
                {gmError}
              </div>
            )}

            {!isViewer && (
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[160px]">
                  <Field label="Add member">
                    <select
                      className={inputCls}
                      value={addMemberId}
                      onChange={(e) => setAddMemberId(e.target.value)}
                    >
                      <option value="">— Select member —</option>
                      {availableMembers.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.first_name} {m.last_name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="w-32">
                  <Field label="Role">
                    <input
                      className={inputCls}
                      value={addRole}
                      onChange={(e) => setAddRole(e.target.value)}
                      placeholder="member"
                    />
                  </Field>
                </div>
                <button
                  onClick={addMember}
                  disabled={gmBusy || !addMemberId}
                  className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-3 py-2 flex items-center gap-1"
                >
                  <Plus size={16} /> Add
                </button>
              </div>
            )}

            <div>
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                Current members
              </h4>
              {gmLoading ? (
                <div className="p-4 text-center text-slate-400 text-sm">Loading…</div>
              ) : groupMembers.length === 0 ? (
                <div className="p-4 text-center text-slate-400 text-sm">
                  No members in this group yet.
                </div>
              ) : (
                <ul className="space-y-1">
                  {groupMembers.map((gm) => (
                    <li
                      key={gm.id}
                      className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2"
                    >
                      <div>
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                          {gm.first_name || gm.last_name
                            ? `${gm.first_name || ''} ${gm.last_name || ''}`.trim()
                            : memberName(gm.member_id)}
                        </span>
                        <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                          {gm.role}
                        </span>
                      </div>
                      {!isViewer && (
                        <button
                          onClick={() => removeMember(gm.member_id)}
                          disabled={gmBusy}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-60"
                          title="Remove"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
